import { useCoordination } from '@atlassiansox/engagekit-ts';
import React, { useCallback, useEffect, useState, type FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { useCoordinationClient } from '@confluence/engagement-provider';

const i18n = defineMessages({
	spotlightAction: {
		id: 'highlight-actions.create-inline-comment.inline-comment-on-inline-node-spotlight.spotlight-action',
		defaultMessage: 'Got it',
		description: 'Text for the button to dismiss the spotlight',
	},
	spotlightContent: {
		id: 'highlight-actions.create-inline-comment.inline-comment-on-inline-node-spotlight.spotlight-content',
		defaultMessage:
			'You can now leave comments on inline elements like links, dates, statuses and mentions.',
		description: 'Text for the content of the spotlight',
	},
});

export const INLINE_COMMENT_ON_INLINE_NODE_SPOTLIGHT_TARGET =
	'highlight-actions-create-inline-comment-button';

/*
 * Engagement Platform message ID for the inline comment on inline node spotlight
 * prod: https://engage-api.prod.atl-paas.net/#/messages/inline-comments-on-inline-node-spotlight
 * stg:  https://engage-api.staging.atl-paas.net/#/messages/inline-comments-on-inline-node-spotlight
 */
export const INLINE_COMMENT_ON_INLINE_NODE_SPOTLIGHT_MESSAGE_ID =
	'inline-comments-on-inline-node-spotlight';

export interface InlineCommentOnInlineNodeSpotlightProps {
	onClickSpotlightTarget: (eventData: {
		event: React.MouseEvent<HTMLElement, MouseEvent>;
		target?: string;
	}) => void;
}

export const InlineCommentOnInlineNodeSpotlight: FC<InlineCommentOnInlineNodeSpotlightProps> = ({
	onClickSpotlightTarget,
}) => {
	const coordinationClient = useCoordinationClient();
	const { formatMessage } = useIntl();
	const [isSpotlightOn, setIsSpotlightOn] = useState(false);
	const [started, stop] = useCoordination(
		coordinationClient,
		INLINE_COMMENT_ON_INLINE_NODE_SPOTLIGHT_MESSAGE_ID,
	);

	useEffect(() => {
		// only fire exposure event when the target user is in audience
		if (started && editorExperiment('comment_on_inline_node_spotlight', true, { exposure: true })) {
			setIsSpotlightOn(true);
		}
	}, [started]);

	const onClose = useCallback(() => {
		setIsSpotlightOn(false);
		void stop();
	}, [stop, setIsSpotlightOn]);

	return (
		<SpotlightTransition>
			{isSpotlightOn && (
				<Spotlight
					target={INLINE_COMMENT_ON_INLINE_NODE_SPOTLIGHT_TARGET}
					key="highlight-actions-create-inline-comment-button"
					testId="highlight-actions-create-inline-comment-spotlight"
					dialogPlacement="top center"
					actions={[
						{
							onClick: onClose,
							text: formatMessage(i18n.spotlightAction),
						},
					]}
					dialogWidth={275}
					targetOnClick={onClickSpotlightTarget}
				>
					{formatMessage(i18n.spotlightContent)}
				</Spotlight>
			)}
		</SpotlightTransition>
	);
};
